import { SiteUrls } from '@tableau/shared-urls';
import { ButtonType, ButtonWidget, DialogPositioningBehavior, DialogType, DialogWidget } from '@tableau/widgets';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { TableauExpressionFormatter } from '../tableauExpression/components/TableauExpressionFormatter';
import { SheetFieldWithCalculation } from './SheetField';
import {
  CalculationDialogCopiedText,
  calculationDialogCopiedTextAnimationDuration,
  CalculationDialogFooter,
  CalculationDialogHeader,
  CalculationDialogTextBox,
  CalculationDialogTypeInfo,
  CalulationDialogTextBoxLabel
} from './VizClientFieldCardStyles';

export type CalculationDialogProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly field: SheetFieldWithCalculation;
  readonly workbookVizportalUrlId?: string;
  readonly siteUrls: SiteUrls;
};

export function CalculationDialog(props: CalculationDialogProps): JSX.Element | null {
  const [hasBeenCopied, setCopiedState] = React.useState(false);
  const fieldIcon = iconForField(props.field);

  const copyToClipboard: React.MouseEventHandler<HTMLButtonElement> = e => {
    const temporaryTextArea = document.createElement('textarea');
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    temporaryTextArea.innerText = props.field.formula!;
    document.body.appendChild(temporaryTextArea);
    temporaryTextArea.select();
    document.execCommand('copy');
    setCopiedState(true);
    temporaryTextArea.remove();
    // refocus the copy button so focus doesn't fall on body
    e.currentTarget.focus();
    setTimeout(() => {
      setCopiedState(false);
    }, calculationDialogCopiedTextAnimationDuration);
  };

  return props.isOpen ? (
    <DialogWidget
      testId={'Calcluation-Lightbox-Dialog'}
      title={Messages.DataDetailsCalculation()}
      type={DialogType.NormalWithTitleBar}
      positioningBehavior={DialogPositioningBehavior.Centered}
      dialogWidth={500}
      showCloseButton={true}
      showFooter={false}
      isModal={true}
      onCloseRequest={props.onClose}
      handleGlassClick={props.onClose}
      shouldReturnFocusOnClose={true}
    >
      <CalculationDialogHeader>{props.field.name}</CalculationDialogHeader>
      <CalculationDialogTypeInfo>
        <SvgFieldIcon fieldIcon={fieldIcon} />
        <span>{fieldIcon ? FieldNameMappings[fieldIcon]() : ''}</span>
      </CalculationDialogTypeInfo>
      <CalulationDialogTextBoxLabel>{Messages.DataDetailsCalculation()}</CalulationDialogTextBoxLabel>
      <CalculationDialogTextBox>
        <TableauExpressionFormatter
          // The code was like this before the lint rule was enabled
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          expression={props.field.formula!}
          fields={props.field.fields}
          parameters={props.field.parameters}
          makeFieldUrl={(fieldId) =>
            props.workbookVizportalUrlId
              ? props.siteUrls.workbookLineageFieldUrl(props.workbookVizportalUrlId, fieldId)
              : ''
          }
          openLinksInNewTab={true}
        />
      </CalculationDialogTextBox>
      <CalculationDialogFooter>
        {hasBeenCopied && (
          <CalculationDialogCopiedText
            role={'alert'}
          >
            {Messages.DataDetailsCalculationCopiedToClipboard()}
          </CalculationDialogCopiedText>
        )}
        <ButtonWidget
          buttonType={ButtonType.Primary}
          testId={'Calculation-Dialog-Copy'}
          onClick={copyToClipboard}
        >
          {Messages.DialogCopy()}
        </ButtonWidget>
      </CalculationDialogFooter>
    </DialogWidget>
  ) : null;
}
